import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound } from 'lucide-react';
import { api, ApiError } from '../api/cliente.js';
import { Botao } from '../componentes/ui/Botao.js';
import { Modal } from '../componentes/ui/Modal.js';
import { Pill, type TomPill } from '../componentes/ui/Pill.js';

interface Token {
  id: string;
  nome: string;
  mascara: string;
  escopo: string[];
  operadora: { id: string; nome: string } | null;
  expiraEm: string | null;
  ultimoUso: string | null;
  revogadoEm: string | null;
  expirado: boolean;
  criadoEm: string;
}

export function Tokens() {
  const qc = useQueryClient();
  const [novoAberto, setNovoAberto] = useState(false);
  const [gerado, setGerado] = useState<{ nome: string; token: string } | null>(null);

  const { data: tokens = [], isLoading } = useQuery({
    queryKey: ['tokens'],
    queryFn: () => api.get<Token[]>('/tokens'),
  });

  const revogar = useMutation({
    mutationFn: (id: string) => api.post(`/tokens/${id}/revogar`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tokens'] }),
  });

  if (isLoading) return <Esqueleto />;

  const estadoDe = (t: Token): { rotulo: string; tom: TomPill } => {
    if (t.revogadoEm) return { rotulo: 'Revogado', tom: 'erro' };
    if (t.expirado) return { rotulo: 'Expirado', tom: 'aviso' };
    return { rotulo: 'Ativo', tom: 'ok' };
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[13px] text-texto-suave">
          Chaves para integrações externas usarem a API do painel.
        </p>
        <Botao variante="primario" onClick={() => setNovoAberto(true)}>
          Gerar token
        </Botao>
      </div>

      {tokens.length === 0 ? (
        <div className="rounded-card border border-borda bg-superficie px-6 py-14 text-center">
          <KeyRound size={24} className="mx-auto mb-2 text-texto-mudo" />
          <p className="text-[15px] font-extrabold">Nenhum token gerado</p>
          <p className="mx-auto mt-1 max-w-[420px] text-[13px] text-texto-suave">
            Use tokens para que sistemas externos consultem ou alterem reversos sem uma conta de
            usuário.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-card border border-borda bg-superficie">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse">
              <thead>
                <tr className="border-b border-borda bg-borda-tenue">
                  {['Nome', 'Token', 'Escopo', 'Último uso', 'Situação', ''].map((h) => (
                    <th
                      key={h}
                      className="px-[18px] py-3 text-left text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => {
                  const e = estadoDe(t);
                  const inativo = !!t.revogadoEm || t.expirado;
                  return (
                    <tr
                      key={t.id}
                      className={`border-t border-borda-suave ${inativo ? 'opacity-55' : ''}`}
                    >
                      <td className="px-[18px] py-[13px]">
                        <p className="text-[13.5px] font-bold">{t.nome}</p>
                        {t.operadora && (
                          <p className="text-[11.5px] text-texto-suave">{t.operadora.nome}</p>
                        )}
                      </td>
                      <td className="px-[18px] py-[13px] font-mono text-[12px]">{t.mascara}</td>
                      <td className="px-[18px] py-[13px]">
                        <span className="text-[12.5px]">{t.escopo.length} permissões</span>
                      </td>
                      <td className="px-[18px] py-[13px] text-[12.5px] text-texto-suave">
                        {t.ultimoUso
                          ? new Date(t.ultimoUso).toLocaleString('pt-BR', { dateStyle: 'short' })
                          : 'nunca usado'}
                      </td>
                      <td className="px-[18px] py-[13px]">
                        <Pill tom={e.tom}>{e.rotulo}</Pill>
                      </td>
                      <td className="px-[18px] py-[13px] text-right">
                        {!inativo && (
                          <Botao
                            tamanho="tabela"
                            variante="perigo"
                            onClick={() => revogar.mutate(t.id)}
                          >
                            Revogar
                          </Botao>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="rounded-card border border-borda bg-superficie px-4 py-3 text-[12.5px] text-texto-suave">
        Base da API: <code className="mono">https://rdns1.consultnetworks.com.br/api</code> ·
        autenticação por cabeçalho <code className="mono">Authorization: Bearer &lt;token&gt;</code>
      </p>

      {novoAberto && (
        <ModalNovo
          aoFechar={() => setNovoAberto(false)}
          aoCriar={(d) => {
            void qc.invalidateQueries({ queryKey: ['tokens'] });
            setGerado(d);
          }}
        />
      )}

      {gerado && <ModalGerado dados={gerado} aoFechar={() => setGerado(null)} />}
    </div>
  );
}

function ModalNovo({
  aoFechar,
  aoCriar,
}: {
  aoFechar: () => void;
  aoCriar: (d: { nome: string; token: string }) => void;
}) {
  const [nome, setNome] = useState('');
  const [escopo, setEscopo] = useState<string[]>([]);
  const [dias, setDias] = useState<string>('');

  const { data: permissoes = [] } = useQuery({
    queryKey: ['tokens', 'permissoes'],
    queryFn: () => api.get<{ valor: string; grupo: string }[]>('/tokens/permissoes'),
  });

  const criar = useMutation({
    mutationFn: () =>
      api.post<{ nome: string; token: string }>('/tokens', {
        nome,
        escopo,
        expiraEmDias: dias ? Number(dias) : undefined,
      }),
    onSuccess: (r) => {
      aoCriar(r);
      aoFechar();
    },
  });

  const grupos = [...new Set(permissoes.map((p) => p.grupo))];

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      largura="largo"
      titulo="Gerar token"
      descricao="O valor completo aparece uma única vez, logo após a criação."
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            onClick={() => criar.mutate()}
            carregando={criar.isPending}
            disabled={nome.length < 2 || escopo.length === 0}
          >
            Gerar
          </Botao>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <Rotulo>Nome</Rotulo>
          <input
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            autoFocus
            placeholder="Integração provisionamento"
            className="h-11 w-full rounded-controle border border-borda px-3 text-[14px] focus:border-marca focus:outline-none"
          />
        </label>

        <label className="block">
          <Rotulo>Expira em (dias)</Rotulo>
          <input
            value={dias}
            onChange={(e) => setDias(e.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
            placeholder="deixe vazio para não expirar"
            className="h-11 w-full rounded-controle border border-borda px-3 text-[14px] focus:border-marca focus:outline-none"
          />
        </label>

        <div>
          <Rotulo>Permissões</Rotulo>
          {/* Marcar o mínimo necessário é o ponto: um token com escopo amplo
              vazado dá a quem o encontrar tudo que ele permite. */}
          <p className="mb-2 text-[12px] text-texto-suave">
            Conceda apenas o que a integração precisa.
          </p>
          <div className="max-h-64 space-y-3 overflow-y-auto rounded-chip border border-borda p-3">
            {grupos.map((g) => (
              <div key={g}>
                <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.5px] text-texto-mudo">
                  {g}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {permissoes
                    .filter((p) => p.grupo === g)
                    .map((p) => {
                      const marcado = escopo.includes(p.valor);
                      return (
                        <button
                          key={p.valor}
                          type="button"
                          onClick={() =>
                            setEscopo((s) =>
                              marcado ? s.filter((v) => v !== p.valor) : [...s, p.valor],
                            )
                          }
                          className={`rounded-chip border px-2 py-1 font-mono text-[11.5px] ${
                            marcado
                              ? 'border-marca bg-tint text-marca'
                              : 'border-borda text-texto-suave hover:border-marca'
                          }`}
                        >
                          {p.valor}
                        </button>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {criar.error && (
          <p className="rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
            {(criar.error as ApiError).mensagem}
          </p>
        )}
      </div>
    </Modal>
  );
}

function ModalGerado({
  dados,
  aoFechar,
}: {
  dados: { nome: string; token: string };
  aoFechar: () => void;
}) {
  const [copiado, setCopiado] = useState(false);

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo="Token gerado"
      descricao="Copie agora — ele não será exibido de novo."
      rodape={<Botao variante="primario" onClick={aoFechar}>Já copiei</Botao>}
    >
      <div className="space-y-3">
        <p className="text-[13px]">
          <strong>{dados.nome}</strong>
        </p>
        <div className="flex gap-2">
          <p className="flex-1 select-all break-all rounded-chip bg-fundo px-3 py-2.5 font-mono text-[12.5px]">
            {dados.token}
          </p>
          <Botao
            iconeEsquerda={<Copy size={15} />}
            onClick={() => {
              void navigator.clipboard.writeText(dados.token);
              setCopiado(true);
            }}
          >
            {copiado ? 'Copiado' : 'Copiar'}
          </Botao>
        </div>
        <p className="rounded-chip bg-aviso-fundo px-3 py-2 text-[12.5px] leading-snug text-aviso">
          O painel guarda apenas o hash. Se perder este valor, gere um token novo — não há como
          recuperá-lo.
        </p>
      </div>
    </Modal>
  );
}

function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
      {children}
    </span>
  );
}

function Esqueleto() {
  return <div className="h-64 animate-pulse rounded-card border border-borda bg-superficie" />;
}
